type MetaResponse = {
	version: string
}

type CacheBusterOptions = {
	enableLogging?: boolean
}

async function withRetry<T>(
	fn: () => Promise<T>,
	options: { attempts?: number; backoff?: number; onFinalError?: (error: unknown) => void } = {},
): Promise<T | null> {
	const { attempts = 3, backoff = 100, onFinalError } = options

	for (let attempt = 0; attempt < attempts; attempt++) {
		try {
			return await fn()
		} catch (error) {
			if (attempt === attempts - 1) {
				onFinalError?.(error)

				return null
			}

			await new Promise((resolve) => setTimeout(resolve, backoff * (attempt + 1)))
		}
	}

	return null
}

/**
 * Fetches the current app version from meta.json
 * @returns The version string from meta.json, or 'unknown' if fetch fails
 */
export async function getVersion(): Promise<string> {
	const version = await withRetry(
		async () => {
			const response = await fetch(`/meta.json?${new Date().getTime()}`, { cache: 'no-store' })

			if (!response.ok) {
				throw new Error(`HTTP ${response.status}`)
			}

			const data: MetaResponse = await response.json()

			return data.version
		},
		{
			onFinalError: (error) => {
				// eslint-disable-next-line no-console
				console.error('* Failed to fetch version from meta.json:', error)
			},
		},
	)

	return version ?? 'unknown'
}

const runCacheBuster = async (options: CacheBusterOptions = {}): Promise<void> => {
	const { enableLogging = false } = options
	const currentVersion = localStorage.getItem('appVersion')

	const log = (message: string | Error, isError = false): void => {
		if (enableLogging) {
			const formattedMessage = message instanceof Error ? message : `[cache-buster] ${message}`

			if (isError) {
				// eslint-disable-next-line no-console
				console.error(formattedMessage)
			} else {
				// eslint-disable-next-line no-console
				console.log(formattedMessage)
			}
		}
	}

	const isThereNewVersion = (metaVersion: string, currentVersion: string | null): boolean => {
		return currentVersion !== null && metaVersion !== currentVersion
	}

	const refreshCacheAndReload = (newVersion: string): void => {
		try {
			localStorage.setItem('appVersion', newVersion)
			window.location.hash = `#${new Date().getTime()}`
			window.location.reload()
		} catch (error) {
			log('An error occurred while refreshing the cache.', true)
			if (error instanceof Error) {
				log(error, true)
			}
			throw error
		}
	}

	try {
		const metaVersion = await getVersion()

		if (metaVersion === 'unknown') {
			log('Could not determine version, skipping cache check.', true)

			return
		}

		const shouldForceRefresh = isThereNewVersion(metaVersion, currentVersion)

		if (shouldForceRefresh) {
			log(`There is a new version: ${metaVersion}. Should force refresh.`)
			refreshCacheAndReload(metaVersion)

			return
		}

		log('There is no new version. No cache refresh needed.')
		localStorage.setItem('appVersion', metaVersion)
	} catch (error) {
		log('An error occurred while checking cache status.', true)
		if (error instanceof Error) {
			log(error, true)
		}
	}
}

export { runCacheBuster }
